"use client";

import React, { useCallback, useState } from "react";
import Link from "next/link";
import { useQuery } from "@apollo/client/react";
import { Route, Pause, Play, Square, Plus } from "lucide-react";
import { Button } from "@wtc/ui";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Contacts } from "@/src/gql/graphql";
import { GetEnrollmentsForContactDocument } from "@/src/gql/graphql";
import { useSequencesAccess } from "@/lib/features/use-sequences-access";

type EnrollAction = "pause" | "resume" | "bail";

const STATUS_STYLE: Record<string, string> = {
  active: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
  paused: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
  replied: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
  completed: "bg-slate-100 text-slate-600",
  bailed: "bg-slate-100 text-slate-500",
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago",
    dateStyle: "medium",
  }).format(new Date(iso));
}

export const ContactSequenceDisplay: React.FC<TabContentProps<Contacts>> = ({
  item: contact,
}) => {
  const canSee = useSequencesAccess();
  const { data, loading, refetch } = useQuery(GetEnrollmentsForContactDocument, {
    variables: { contactId: contact.id },
    skip: !canSee || !contact.id,
    fetchPolicy: "cache-and-network",
  });
  const [busy, setBusy] = useState<string | null>(null);

  const act = useCallback(
    async (enrollmentId: string, action: EnrollAction) => {
      setBusy(enrollmentId);
      try {
        await fetch(`/api/sequences/enrollments/${enrollmentId}/action`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action }),
        });
        await refetch();
      } finally {
        setBusy(null);
      }
    },
    [refetch],
  );

  if (!canSee) {
    return <div className="p-6 text-sm text-muted-foreground">Not available.</div>;
  }

  const enrollments = (data?.sales_sequence_enrollmentsCollection?.edges ?? []).map(
    (e) => e.node,
  );

  return (
    <div className="min-h-[300px] space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-base md:text-lg font-semibold min-w-0">
          <Route className="h-5 w-5 shrink-0 text-[var(--primary)]" /> Sequences
        </h3>
        <Link href={`/sequences?enrollContactId=${contact.id}`} className="shrink-0">
          <Button size="sm" variant="outline" className="h-11 sm:h-8">
            <Plus className="mr-1 h-4 w-4" /> Enroll in cadence
          </Button>
        </Link>
      </div>

      {loading && enrollments.length === 0 && (
        <p className="text-sm text-muted-foreground">Loading…</p>
      )}

      {!loading && enrollments.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Not on any cadence yet. Enroll them to start a multi-touch sequence.
        </p>
      )}

      <div className="space-y-3">
        {enrollments.map((e) => {
          const steps =
            e.sales_sequences?.sales_sequence_stepsCollection?.edges?.map((s) => s.node) ?? [];
          const total = steps.length;
          const events =
            e.sales_sequence_step_eventsCollection?.edges?.map((ev) => ev.node) ?? [];
          const isLive = e.status === "active" || e.status === "paused" || e.status === "replied";
          return (
            <div key={e.id} className="rounded-lg border bg-card p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="font-medium">{e.sales_sequences?.name ?? "Sequence"}</span>
                  <span
                    className={`ml-2 inline-flex rounded-full px-2 py-0.5 text-xs ${
                      STATUS_STYLE[e.status] ?? "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {e.status}
                  </span>
                </div>
                <div className="text-sm text-muted-foreground">
                  Step {e.current_step_number}
                  {total ? ` / ${total}` : ""}
                  {e.status === "active" && e.next_action_at
                    ? ` · next ${fmtDate(e.next_action_at)}`
                    : ""}
                </div>
              </div>

              {e.pause_reason && (
                <p className="mt-1 text-xs text-amber-700">{e.pause_reason}</p>
              )}

              {events.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                  {events.slice(0, 5).map((ev) => (
                    <li key={ev.id}>
                      Step {ev.step_number} · {ev.channel} · {ev.outcome} — {fmtDate(ev.occurred_at)}
                    </li>
                  ))}
                </ul>
              )}

              {isLive && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {e.status === "active" ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-11 sm:h-8"
                      disabled={busy === e.id}
                      onClick={() => void act(e.id, "pause")}
                    >
                      <Pause className="mr-1 h-4 w-4" /> Pause
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-11 sm:h-8"
                      disabled={busy === e.id}
                      onClick={() => void act(e.id, "resume")}
                    >
                      <Play className="mr-1 h-4 w-4" /> Resume
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-11 sm:h-8"
                    disabled={busy === e.id}
                    onClick={() => void act(e.id, "bail")}
                  >
                    <Square className="mr-1 h-4 w-4 text-rose-600" /> Stop
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
